import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import Seo from "@/components/common/Seo";
import { Loader2, CheckCircle2, XCircle, Car } from "lucide-react";

/**
 * Car-valuation Stripe return page.
 * Polls GET /car-valuation/checkout/status/{session_id}.
 */
export default function CarValuationSuccessPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const sessionId = sp.get("session_id");
  const [attempts, setAttempts] = useState(0);
  const [pending, setPending] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("car_valuation_pending") || "null"); } catch { return null; }
  });

  const q = useQuery({
    queryKey: ["car-valuation-status", sessionId, attempts],
    queryFn: async () => (await sharedApi.get(`/classifieds/car-valuation/checkout/status/${sessionId}`)).data,
    enabled: !!sessionId,
    retry: 0,
  });

  const status = q.data?.payment_status;
  const isPaid = status === "paid";
  const isFailed = status === "unpaid" || status === "expired" || status === "canceled";
  const isPolling = !isPaid && !isFailed && attempts < 10;

  useEffect(() => {
    if (!sessionId || !isPolling) return;
    const t = setTimeout(() => setAttempts((a) => a + 1), 2000);
    return () => clearTimeout(t);
  }, [sessionId, isPolling, attempts]);

  useEffect(() => {
    if (isPaid) {
      try { sessionStorage.removeItem("car_valuation_pending"); } catch { /* ignore */ }
      // Auto-return to valuation page with the plate they wanted
      if (pending?.plate) {
        const t = setTimeout(() => nav(`/car-valuation?plate=${encodeURIComponent(pending.plate)}`), 1500);
        return () => clearTimeout(t);
      }
    }
    if (isFailed) {
      try { sessionStorage.removeItem("car_valuation_pending"); } catch { /* ignore */ }
    }
  }, [isPaid, isFailed, pending, nav]);

  if (!sessionId) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <XCircle className="w-10 h-10 text-red-500 mx-auto" />
        <h1 className="mt-3 font-display text-2xl font-bold text-slate-900">Missing session</h1>
        <Link
          to="/car-valuation"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
        >
          Back to Car Valuation
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="car-valuation-return">
      <Seo title="Car Valuation payment" description="Confirming your credit purchase." />

      {isPolling && (
        <>
          <Loader2 className="w-10 h-10 text-[color:var(--brand-blue)] mx-auto animate-spin" />
          <h1 className="mt-4 font-display text-2xl font-bold text-slate-900" data-testid="car-valuation-checking">
            Confirming payment…
          </h1>
        </>
      )}

      {isPaid && (
        <>
          <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 text-emerald-600 grid place-items-center">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h1 className="mt-4 font-display text-3xl font-black text-slate-900" data-testid="car-valuation-success">
            Credits added! 🎉
          </h1>
          <p className="mt-2 text-slate-600">
            {pending?.plate ? <>Reopening the valuation for <span className="font-semibold text-slate-900">{pending.plate}</span>…</> : "Ready to value your car."}
          </p>
          <Link
            to={pending?.plate ? `/car-valuation?plate=${encodeURIComponent(pending.plate)}` : "/car-valuation"}
            data-testid="car-valuation-continue"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white font-bold text-sm px-5 py-2.5 transition-colors"
          >
            <Car className="w-4 h-4" /> Continue
          </Link>
        </>
      )}

      {(isFailed || (!isPaid && !isPolling)) && (
        <>
          <XCircle className="w-10 h-10 text-red-500 mx-auto" />
          <h1 className="mt-3 font-display text-2xl font-bold text-slate-900" data-testid="car-valuation-failed">
            Payment didn&apos;t go through
          </h1>
          <p className="mt-2 text-slate-500">No charge was made.</p>
          <Link
            to="/car-valuation"
            data-testid="car-valuation-back"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-5 py-2.5 transition-colors"
          >
            Back to Car Valuation
          </Link>
        </>
      )}
    </div>
  );
}
